import { Button } from "components/Button/Button";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { Form } from "components/Form/components/Form/Form";
import { FormComponentProps } from "components/Form/components/FormComponentProps";
import { IconCollection } from "components/Icon/IconCollection";
import { CommitGeneratorField } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/formComponents/CommitGeneratorField";
import { UncommittedResourceListField } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/formComponents/UncommittedResourceListField";
import {
    UncommittedData,
    generateMessage,
} from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { FormApi } from "final-form";
import { useEffect, useMemo, useState } from "react";
import { Field } from "react-final-form";
import styles from "../../GitAction.module.scss";

interface BulkCommitFormProps extends FormComponentProps {
    uncommittedRecordList: UncommittedData[];
    closeModal: () => void;
    handleSubmit: () => Promise<any>;
    pristine: boolean;
    hasValidationErrors: boolean;
    values: { commit_message: string; records: UncommittedData[] };
    form: FormApi;
    submitSucceeded: boolean;
}

export const BulkCommitForm = ({
    uncommittedRecordList,
    closeModal,
    handleSubmit,
    pristine,
    hasValidationErrors,
    submitSucceeded,
    values,
    form,
}: BulkCommitFormProps) => {
    const [isGenerateMessageButtonDisabled, setGenerateMessageButtonDisabled] = useState(false);
    const [isRecordFormDisabled, setRecordFormDisabled] = useState(false);
    const [shouldResetSelection, setResetSelection] = useState(false);
    const [isMessageAutoGenerated, setMessageAutoGenerated] = useState(true);
    const [allRecordsSelected, setAllRecordsSelected] = useState(true);

    const generatedMessage = useMemo(
        () => generateMessage(allRecordsSelected ? uncommittedRecordList : values.records),
        [allRecordsSelected, uncommittedRecordList, values.records]
    );

    useEffect(() => {
        if (values.records.length === 0) {
            setResetSelection(false);
        }
    }, [values.records]);

    useEffect(() => {
        if (allRecordsSelected) {
            values.records = [];
        }
    }, [allRecordsSelected, values]);

    useEffect(() => {
        if (isMessageAutoGenerated) {
            values.commit_message = "";
        }
    }, [allRecordsSelected, isMessageAutoGenerated, values]);

    return (
        <Form onSubmit={handleSubmit}>
            <div
                className={`${styles.scrollableContent} ${
                    submitSucceeded ? styles.disabledForm : ""
                }`}
            >
                <Form.Row>
                    <Field
                        name="records"
                        id="records"
                        fieldProps={{}}
                        fieldAttr={{ disabled: isRecordFormDisabled }}
                    >
                        {(props: FormComponentProps) => (
                            <UncommittedResourceListField
                                uncommittedRecordList={uncommittedRecordList}
                                shouldResetSelection={shouldResetSelection}
                                allRecordsSelected={allRecordsSelected}
                                setAllRecordsSelected={setAllRecordsSelected}
                                {...props}
                            />
                        )}
                    </Field>
                </Form.Row>
                <Form.Row>
                    <Field
                        name="commit_message"
                        id="commit_message"
                        fieldProps={{
                            label: "Commit message:",
                        }}
                        fieldAttr={{
                            placeholder: "Select resources to generate commit message",
                        }}
                    >
                        {(props: FormComponentProps) => (
                            <CommitGeneratorField
                                {...props}
                                generatedMessage={generatedMessage}
                                isDisabled={
                                    (allRecordsSelected === false && values.records.length === 0) ||
                                    isGenerateMessageButtonDisabled
                                }
                                disableButtonAndForm={() => {
                                    setGenerateMessageButtonDisabled(true);
                                    setRecordFormDisabled(true);
                                }}
                                setMessageAutoGenerated={setMessageAutoGenerated}
                                isMessageAutoGenerated={isMessageAutoGenerated}
                            />
                        )}
                    </Field>
                </Form.Row>
            </div>
            <Form.Row>
                <CancelButton onClick={() => closeModal()} />
                <Button
                    onClick={() => {
                        setGenerateMessageButtonDisabled(false);
                        setRecordFormDisabled(false);
                        setResetSelection(true);
                        form.change("commit_message", "");
                    }}
                    variation="white"
                    label="Back to selection"
                    disabled={values.records.length === 0}
                />
                <Button
                    onClick={() => {
                        handleSubmit();
                        closeModal();
                    }}
                    loading={submitSucceeded}
                    variation="primary"
                    label="Bulk commit"
                    icon={IconCollection.uploadMultiple}
                />
            </Form.Row>
        </Form>
    );
};
